import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  type NotificationPreferences,
} from '@/api/hooks/useAccount';
import type { AppError } from '@/api/client';

/**
 * Notification-preferences card. Two grouped sets of toggles:
 *
 * <ul>
 *   <li>Email — always editable.</li>
 *   <li>SMS — only shown when the user has a verified phone. If
 *       not verified, render a hint pointing them at the phone
 *       section above.</li>
 * </ul>
 *
 * Local state holds the in-progress edits; saves push the whole
 * DTO at once so partial-update semantics aren't a thing customers
 * have to reason about.
 */
export default function NotificationPreferencesSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, isLoading } = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();

  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);

  useEffect(() => {
    if (data) setPrefs(data);
  }, [data]);

  const hasVerifiedPhone = !!user?.phone && !!user?.phoneVerified;

  const set = (key: keyof NotificationPreferences, value: boolean) => {
    if (!prefs) return;
    setPrefs({ ...prefs, [key]: value });
  };

  const save = () => {
    if (!prefs) return;
    update.mutate(prefs, {
      onSuccess: () => toast.success(t('account.preferences_saved')),
      onError: (err) => {
        const e = err as unknown as AppError;
        toast.error(e?.message ?? t('common.error'));
      },
    });
  };

  if (isLoading || !prefs) {
    return (
      <section id="notifications" className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-500">
          {t('account.notifications')}
        </h2>
        <p className="text-sm text-neutral-400">{t('common.loading')}</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {t('account.notifications')}
      </h2>

      <div className="mb-4">
        <h3 className="mb-2 text-sm font-medium text-neutral-700">
          {t('account.email_notifications')}
        </h3>
        <div className="space-y-1">
          <Toggle
            label={t('account.booking_confirmation')}
            checked={prefs.emailBookingConfirmation}
            onChange={(v) => set('emailBookingConfirmation', v)}
          />
          <Toggle
            label={t('account.session_reminder')}
            checked={prefs.emailSessionReminder}
            onChange={(v) => set('emailSessionReminder', v)}
          />
          <Toggle
            label={t('account.session_warning')}
            checked={prefs.emailSessionWarning}
            onChange={(v) => set('emailSessionWarning', v)}
          />
          <Toggle
            label={t('account.cancellation')}
            checked={prefs.emailCancellation}
            onChange={(v) => set('emailCancellation', v)}
          />
          <Toggle
            label={t('account.receipt')}
            checked={prefs.emailReceipt}
            onChange={(v) => set('emailReceipt', v)}
          />
        </div>
      </div>

      <div className="mb-4">
        <h3 className="mb-2 text-sm font-medium text-neutral-700">
          {t('account.sms_notifications')}
        </h3>
        {hasVerifiedPhone ? (
          <div className="space-y-1">
            <Toggle
              label={t('account.booking_confirmation')}
              checked={prefs.smsBookingConfirmation}
              onChange={(v) => set('smsBookingConfirmation', v)}
            />
            <Toggle
              label={t('account.session_reminder')}
              checked={prefs.smsSessionReminder}
              onChange={(v) => set('smsSessionReminder', v)}
            />
            <Toggle
              label={t('account.session_warning')}
              checked={prefs.smsSessionWarning}
              onChange={(v) => set('smsSessionWarning', v)}
            />
            <Toggle
              label={t('account.cancellation')}
              checked={prefs.smsCancellation}
              onChange={(v) => set('smsCancellation', v)}
            />
          </div>
        ) : (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {t('account.sms_requires_phone')}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={save}
        disabled={update.isPending}
        className="rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {update.isPending ? t('common.saving') : t('account.save_preferences')}
      </button>
    </section>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-50">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-neutral-300 text-[#C2713A] focus:ring-[#C2713A]/30"
      />
      <span className="text-sm text-neutral-700">{label}</span>
    </label>
  );
}
